// [PURPOSE]: Full-screen crash fallback for the outermost Sentry.ErrorBoundary.
// [WHY]: When an error escapes ALL inner ErrorBoundaries, the user needs a friendly
//        screen with an error ID they can reference in support emails, plus a way to
//        reload or export their data before it's lost.
// [INTEGRATION]: Used as fallback in Sentry.ErrorBoundary in src/index.js.
//                The inner ErrorBoundary (src/components/ErrorBoundary.js) handles
//                component-level errors; this catches catastrophic app-level failures.
// [CREATED]: Session 6 — Observability Wiring (April 2026)

import React, { useState } from 'react';
import { APP_VERSION } from '../constants/version';

/**
 * CrashScreen — catastrophic error fallback
 *
 * Props (from Sentry.ErrorBoundary):
 * @param {Error} error — the caught error
 * @param {string} eventId — Sentry event ID (null if Sentry DSN not configured)
 * @param {function} onReset — resets the error boundary (try again)
 */
const CrashScreen = ({ error, eventId, onReset }) => {
  const [copied, setCopied] = useState(false);

  // [PURPOSE]: Build a support-friendly error report the user can paste into an email.
  const buildErrorReport = () => {
    return [
      `Tabletop Inventory Manager v${APP_VERSION} — Crash Report`,
      `Date: ${new Date().toISOString()}`,
      eventId ? `Error ID: ${eventId}` : 'Error ID: (Sentry not configured)',
      `Error: ${error?.message || 'Unknown error'}`,
      ``,
      `Browser: ${navigator.userAgent}`,
      `Screen: ${window.innerWidth}x${window.innerHeight}`,
      ``,
      `Stack:`,
      error?.stack || 'No stack trace available',
    ].join('\n');
  };

  const handleCopyReport = async () => {
    const report = buildErrorReport();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(report);
      } else {
        // Fallback for older browsers / WebView
        const textarea = document.createElement('textarea');
        textarea.value = report;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silent fail — the report text is displayed on screen so user can manually copy
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  // [PURPOSE]: Attempt to export character data from localStorage so the user
  // doesn't lose their work. This is a best-effort last resort.
  const handleExportData = () => {
    try {
      const keys = Object.keys(localStorage);
      const data = {};
      for (const key of keys) {
        try {
          data[key] = JSON.parse(localStorage.getItem(key));
        } catch {
          data[key] = localStorage.getItem(key);
        }
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tti-emergency-backup-${Date.now()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      // If even localStorage is inaccessible, nothing we can do
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="bg-gray-800/90 border border-red-500/60 rounded-2xl shadow-2xl max-w-lg w-full p-8 backdrop-blur">

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="bg-red-600/20 border border-red-500/40 rounded-full p-4">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-red-300 text-center mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-400 text-center text-sm mb-6">
          The app hit an unexpected error. Your saved data should still be safe.
        </p>

        {/* Error ID — the key piece for support */}
        {eventId && (
          <div className="bg-gray-900/60 border border-gray-700 rounded-lg px-4 py-3 mb-6 text-center">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Error Reference</p>
            <p className="text-amber-300 font-mono text-sm select-all">{eventId}</p>
          </div>
        )}

        {/* Error message (truncated) */}
        <div className="bg-gray-900/40 rounded-lg px-4 py-3 mb-6">
          <p className="text-gray-500 text-xs mb-1">Error</p>
          <p className="text-gray-300 text-sm font-mono break-all line-clamp-3">
            {error?.message || 'Unknown error'}
          </p>
        </div>

        {/* Action buttons */}
        <div className="space-y-3 mb-6">
          {/* Primary: reload */}
          <button
            onClick={handleReload}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Reload App
          </button>

          {/* Secondary row */}
          <div className="flex gap-3">
            {onReset && (
              <button
                onClick={onReset}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                Try Again
              </button>
            )}
            <button
              onClick={handleExportData}
              className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-2.5 px-4 rounded-lg transition-colors"
            >
              Export Data
            </button>
          </div>
        </div>

        {/* Support contact */}
        <div className="border-t border-gray-700 pt-4 text-center">
          <p className="text-gray-500 text-xs mb-2">
            If this keeps happening, contact support with the error reference above:
          </p>
          <a
            href="mailto:silosupport@gmail.com?subject=TTI%20Crash%20Report&body=Error%20ID%3A%20"
            className="text-blue-400 hover:text-blue-300 text-sm underline"
          >
            silosupport@gmail.com
          </a>
          <div className="mt-3">
            <button
              onClick={handleCopyReport}
              className="text-gray-500 hover:text-gray-300 text-xs underline transition-colors"
            >
              {copied ? 'Copied!' : 'Copy full crash report'}
            </button>
          </div>
        </div>

        {/* Version footer */}
        <p className="text-gray-600 text-xs text-center mt-4">
          v{APP_VERSION}
        </p>
      </div>
    </div>
  );
};

export default CrashScreen;
